/** @jsx jsx */
import { Flex, jsx, Styled } from "theme-ui"
import { useEffect, useReducer, useState } from "react"
import PropTypes from "prop-types"
import { Themed } from "@theme-ui/mdx"

// Utils
import slugify from "slugify"
import scrollIntoView from "scroll-into-view"
import theme, { rem } from "@src/theme"
import { isBrowser } from "@helpers"
import { intersection } from "lodash"

// Reducers
import { filtersReducer, initialState } from "./filtersReducer"

// Components
import Pagination from "@components/Pagination"
import Container from "@components/Container"
import { Grid } from "@theme-ui/components"
import Section from "@components/Section"
import ProductCard from "@shop/components/ProductCard"
import Checkbox from "@components/form/Checkbox"
import Radio from "@components/form/Radio"
import Expander from "@components/Expander"

// Assets
import CarrotDown from "@components/assets/svg/carrot-down.svg"

// Constants
const ITEMS_PER_PAGE = 8

const updateSearchParams = (location, { audience, types, flags }) => {
  const typesParams = types
    .join(",")
    .toLowerCase()
    .replace(" + ", " ")
  const flagsParams = flags
    .join(",")
    .toLowerCase()
    .replace(" + ", " ")

  const url = new URL(location.href)
  url.searchParams.set("type", typesParams)
  url.searchParams.set("flag", flagsParams)
  url.searchParams.set("audience", audience)

  history.replaceState({}, "", url)
}

const ProductsCollection = ({
  location,
  products = {},
  productTypes = [],
  audienceTypes = [],
  flagTypes = [],
  maxItems = ITEMS_PER_PAGE,
  initialParams = {
    audience: "pride",
    types: [],
    flags: [],
  },
  ...props
}) => {
  const [filtersState, filtersDispatch] = useReducer(filtersReducer, {
    ...initialState,
    ...initialParams,
  })
  const [currentPage, setCurrentPage] = useState(0)

  const handleFilterClick = (filterType, value) => {
    filtersDispatch({
      type: "changed_filter",
      filterType,
      value,
    })
    setCurrentPage(0)
  }

  const handleClearFilters = () => {
    filtersDispatch({
      type: "clear_filters",
    })
    setCurrentPage(0)
  }

  const handleToggleFilters = () => {
    filtersDispatch({
      type: "toggle_show_filters",
    })
  }

  const handlePageClick = ({ selected }) => {
    setCurrentPage(selected)
    scrollToTop()
  }

  const handleProductClicked = () => {
    updateSearchParams(location, filtersState)
  }

  const scrollToTop = () => {
    if (isBrowser) {
      const element = document.getElementById("products-collection")
      if (element) {
        scrollIntoView(element)
      }
    }
  }

  const getResults = () => {
    // Set results to current selected audience
    let results = [...products[filtersState.audience].products].filter(
      item => item.status === "ACTIVE"
    )

    // Skip if no filters selected
    if (filtersState.types.length || filtersState.flags.length) {
      // Format all tags into lowercase + no special chars
      const formattedTags = [
        ...filtersState.types,
        ...filtersState.flags,
      ].map(tag => slugify(tag.toLowerCase()))

      // Filter by type and flags
      results = results.filter(item => {
        let matches = []
        let itemTags = []

        // Format all item tags into lowercase + no special chars
        itemTags = item.tags.map(tag => slugify(tag.toLowerCase()))

        matches = intersection(itemTags, formattedTags)

        return matches.length
      })
    }

    return results
  }

  const getFiltersCount = () => {
    const audienceSelected = filtersState.audience !== "pride" ? 1 : 0
    return (
      filtersState.types.length + filtersState.flags.length + audienceSelected
    )
  }

  const pageOffset = Math.ceil(currentPage * maxItems)
  const results = getResults()
  const showPagination = results.length > maxItems
  const currentPageResults = results.slice(pageOffset).slice(0, maxItems)
  const filterCount = getFiltersCount()

  return (
    <Section
      sx={{
        minHeight: rem(800),
      }}
      id="products-collection"
    >
      <Container
        sx={{
          display: [null, null, "grid"],
          gridTemplateColumns: ["300px 1fr", "400px 1fr"],
        }}
      >
        <div sx={{ mb: rem(28) }}>
          <h2
            sx={{
              ...theme.styles.h4,
              mt: 0,
              mb: rem(24),
            }}
          >
            Filter Products
          </h2>
          <h3
            sx={{
              ...theme.styles.h6,
              mt: 0,
              mb: rem(12),
            }}
          >
            Item Type
          </h3>
          <div sx={{ mb: rem(28), pl: "1px" }}>
            {productTypes.map(({ label, value }) => (
              <Checkbox
                key={label}
                name="types"
                value={value}
                label={label}
                setFieldValue={handleFilterClick}
                fieldValue={filtersState.types}
                variant="block"
              />
            ))}
          </div>

          <Expander
            open={filtersState.showAllFilters}
            styles={{ mb: filtersState.showAllFilters && rem(22) }}
          >
            <div sx={{ mb: 38, pl: "1px" }}>
              <h3
                sx={{
                  ...theme.styles.h6,
                  mt: 0,
                  mb: rem(12),
                }}
              >
                Audience
              </h3>
              {audienceTypes.map(({ label, value }) => (
                <Radio
                  key={label}
                  name="audience"
                  label={label}
                  value={value}
                  checked={value === filtersState.audience}
                  onRadioChange={e => {
                    handleFilterClick("audience", e.currentTarget.value)
                  }}
                  styles={{
                    bg: "unset",
                    mb: rem(12),
                    "span.checkmark": {
                      borderWidth: "2px",
                      mt: 0,
                    },
                  }}
                />
              ))}
            </div>

            <h3
              sx={{
                ...theme.styles.h6,
                mt: 0,
                mb: rem(16),
              }}
            >
              Flag Type
            </h3>
            {flagTypes.map(({ label, value, icon }) => (
              <Checkbox
                key={label}
                name="flags"
                value={value}
                label={label}
                setFieldValue={handleFilterClick}
                fieldValue={filtersState.flags}
                variant="block"
                icon={icon}
              />
            ))}
          </Expander>

          <button
            onClick={handleToggleFilters}
            sx={{
              display: ["block", null, "none"],
              mb: rem(40),
              color: theme.colors.darkPurple,
            }}
          >
            <div>
              <Themed.h6 sx={{ m: 0 }}>
                Show {filtersState.showAllFilters ? "Less" : "More"} filters
                <CarrotDown
                  sx={{
                    transform: filtersState.showAllFilters
                      ? "translate(8px, -4px) rotate(180deg)"
                      : "translate(5px, -1px)",
                    color: "currentColor",
                    fill: "currentColor",
                    transition: "transform 250ms ease",
                  }}
                />
              </Themed.h6>
            </div>
          </button>
        </div>

        <div>
          {currentPageResults && currentPageResults.length ? (
            <div>
              <Flex
                sx={{
                  alignItems: "flex-start",
                  justifyContent: ["space-between", null, "flex-start"],
                  mt: rem(6),
                }}
              >
                <p sx={{ mt: 0 }}>Showing {results.length + 1} results</p>
                {filterCount ? (
                  <button
                    sx={{
                      variant: "buttons.link",
                      mt: "2px",
                      ml: rem(12),
                    }}
                    onClick={handleClearFilters}
                  >
                    Clear {filterCount} filters
                  </button>
                ) : null}
              </Flex>
              <Grid gap={rem(16)} columns={[2, 2, 2, 3]}>
                {currentPageResults.map(item => {
                  return (
                    <ProductCard
                      key={item.id || item.node.id}
                      data={item}
                      onClick={handleProductClicked}
                    />
                  )
                })}
              </Grid>
            </div>
          ) : (
            <p
              sx={{
                mt: 0,
                textAlign: "center",
              }}
            >
              There are no <span>products</span> to show.
            </p>
          )}
        </div>
      </Container>

      {results && results.length && showPagination ? (
        <Pagination
          pageNumber={currentPage}
          totalItemsCount={results.length}
          ITEMS_PER_PAGE={maxItems}
          handlePageClick={handlePageClick}
        />
      ) : null}
    </Section>
  )
}

ProductsCollection.propTypes = {
  items: PropTypes.array,
  productTypes: PropTypes.array,
}

export default ProductsCollection
